// src/howto/index.js — екран „Как да…?": плочки „искам да…" със стъпки и
// бутон „Покажи ми". Съдържанието е в tasks.js, воденето — в guide.js.
//
// Всеки вижда само своето: задачите минават през същите правила като
// менюто (viewAllowed — роля, права, модули на фирмата; isReadOnlyView —
// „Само преглед"). Клиентът не вижда нищо по Конто, а фирма без доставки —
// задачата за доставките.

import { el, clear } from '../shared/dom.js';
import { state, isReadOnlyView } from '../shared/state.js';
import { viewAllowed, renderShell } from '../shell/shell.js';
import { TASKS, GROUPS, visibleTasks } from './tasks.js';
import { startGuide } from './guide.js';

let _open = null; // id на отворената плочка (помни се при пречертаване)

export function myTasks() {
  const u = state().user || {};
  return visibleTasks({ role: u.role, allowed: (v) => viewAllowed(v), readOnly: (v) => isReadOnlyView(v) }, TASKS);
}

function go(view) {
  const st = state();
  st.view = view;
  st.sidebarOpen = false;
  renderShell();
}

export function view(page) {
  clear(page);
  const list = myTasks();
  if (_open && !list.some((t) => t.id === _open)) _open = null;
  const panel = el('div', { class: 'panel howto' });
  page.appendChild(panel);

  if (_open) { drawTask(panel, list.find((t) => t.id === _open)); return; }

  panel.appendChild(el('h2', { class: 'howto-h', text: 'Какво искате да направите?' }));
  panel.appendChild(el('p', { class: 'howto-sub', text: 'Натиснете върху плочката. После натиснете „▶ Покажи ми“ — програмата ще Ви покаже кой бутон да натиснете.' }));
  // По групи с видимо заглавие: 30 еднакви плочки наведнъж са стена.
  // Група без нито една задача за човека не се показва изобщо.
  const tile = (t) => el('button', {
    class: 'howto-tile', type: 'button',
    onClick: () => { _open = t.id; view(page); focusFirst(page); },
  }, el('span', { class: 'howto-ic', 'aria-hidden': 'true', text: t.icon }), el('span', { text: t.title }));
  const known = new Set(GROUPS.map(([g]) => g));
  const groups = GROUPS.concat([['', 'Други']]);
  groups.forEach(([g, label]) => {
    const own = list.filter((t) => (g ? t.group === g : !known.has(t.group)));
    if (!own.length) return;
    const hId = 'howto-g-' + (g || 'other');
    panel.appendChild(el('section', { class: 'howto-group', 'aria-labelledby': hId },
      el('h3', { class: 'howto-gh', id: hId, text: label }),
      el('div', { class: 'howto-grid' }, ...own.map(tile))));
  });
  if (!list.length) panel.appendChild(el('p', { class: 'howto-sub', text: 'Още няма инструкции за Вашия достъп. Отворете „? Помощ“ горе.' }));
  panel.appendChild(el('div', { class: 'howto-foot' },
    el('button', { class: 'btn ghost', type: 'button', text: '🖨 Принтирай листа', onClick: () => printSheet(list) }),
    el('button', { class: 'btn ghost', type: 'button', text: 'Подробна помощ', onClick: () => go('help') })));
}

function focusFirst(page) {
  const b = page.querySelector('.howto-show');
  if (b) b.focus();
}

function drawTask(panel, t) {
  const page = panel.parentNode;
  panel.appendChild(el('button', { class: 'btn ghost sm howto-back', type: 'button', text: '← Всички', onClick: () => { _open = null; view(page); } }));
  panel.appendChild(el('h2', { class: 'howto-h' }, el('span', { 'aria-hidden': 'true', text: t.icon + ' ' }), t.title));
  if (t.intro) panel.appendChild(el('p', { class: 'howto-intro', text: t.intro }));
  panel.appendChild(el('button', { class: 'btn howto-show', type: 'button', text: '▶ Покажи ми', onClick: () => startGuide(t, { go }) }));
  panel.appendChild(el('ol', { class: 'howto-steps' }, ...t.steps.map(stepItem)));
  if (t.notes && t.notes.length) {
    panel.appendChild(el('section', { class: 'howto-notes', 'aria-labelledby': 'howto-notes-h' },
      el('h3', { id: 'howto-notes-h', text: 'Добре е да знаете' }),
      el('ul', {}, ...t.notes.map((n) => el('li', { text: n })))));
  }
}

// Една стъпка: кратко изречение (то се показва и в „Покажи ми"), под него —
// по-дългото обяснение и полетата едно по едно: „какво пише тук и защо".
// Хората се спъват не в бутона, а в полето („тази цена с ДДС ли е?").
function stepItem(s) {
  const li = el('li', {}, el('span', { class: 'howto-say', text: s.say }));
  if (s.more) li.appendChild(el('p', { class: 'howto-more', text: s.more }));
  if (s.fields && s.fields.length) {
    li.appendChild(el('dl', { class: 'howto-fields' }, ...s.fields.flatMap(([name, what]) => [
      el('dt', { text: name }), el('dd', { text: what }),
    ])));
  }
  return li;
}

// Лист за бюрото: една страница с всички задачи на човека. Отделен прозорец,
// за да не печатаме менюто и лентите на програмата.
function esc(s) { return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]); }
export function sheetHtml(list, who) {
  const order = GROUPS.map(([g]) => g);
  list = list.slice().sort((a, b) => order.indexOf(a.group) - order.indexOf(b.group));
  const items = list.map((t) => '<section><h2>' + esc(t.icon + ' ' + t.title) + '</h2>' +
    (t.intro ? '<p class="in">' + esc(t.intro) + '</p>' : '') + '<ol>' +
    t.steps.map((s) => '<li>' + esc(s.say) +
      (s.fields && s.fields.length ? '<ul>' + s.fields.map(([n, w]) => '<li><b>' + esc(n) + '</b> — ' + esc(w) + '</li>').join('') + '</ul>' : '') +
      '</li>').join('') + '</ol>' +
    (t.notes && t.notes.length ? '<p class="nt"><b>Добре е да знаете:</b> ' + t.notes.map(esc).join(' ') + '</p>' : '') +
    '</section>').join('');
  return '<!doctype html><html lang="bg"><head><meta charset="utf-8"><title>Как да…? — лист за бюрото</title>' +
    '<style>body{font:15px/1.45 Arial,sans-serif;color:#000;margin:18px}h1{font-size:22px;margin:0 0 4px}' +
    '.who{color:#444;margin:0 0 12px}main{columns:2;column-gap:28px}section{break-inside:avoid;margin:0 0 12px}' +
    'h2{font-size:16px;margin:0 0 4px}ol{margin:0;padding-left:20px}li{margin:2px 0}ul{margin:2px 0;padding-left:16px;font-size:13px}' +
    '.in,.nt{margin:0 0 4px;font-size:13px;color:#333}</style></head><body>' +
    '<h1>Как да…? — CRM</h1><p class="who">' + esc(who || '') + '</p><main>' + items + '</main></body></html>';
}

function printSheet(list) {
  const u = state().user || {};
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(sheetHtml(list, u.name || ''));
  w.document.close();
  w.focus();
  w.print();
}
